import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { api } from '../services/api';
import type { OfferingV2Response } from '../types/offeringsV2';
import type { PackageResponse, PackageStatus } from '../types/packages';
import { todayInDubai } from '../components/offers/offerUtils';
import BannerUploader from '../components/offers/BannerUploader';
import ConfirmDialog from '../components/offers/ConfirmDialog';
import { usePackagesT } from '../components/packages/packagesI18n';
import {
  ITEMS_MAX, ITEMS_MIN, RUNNING_STATUSES, SUBMITTABLE_STATUSES,
  derivePricingMode, emptyPackageForm, fixedSaving, formFromPackage, ineligibleReasonOf, packageFormToPreviewRequest,
  packageFormToRequest, packageItemField, validateItemsCount, validatePrice, validateTexts, validateWindow,
  type PackageField, type PackageFieldErrors, type PackageForm, type PackageFormItem,
} from '../components/packages/packageUtils';
import { mapPackageError, packageActionErrorMessage, previewErrorsToFields } from '../components/packages/packageErrors';
import { usePackagePreview } from '../components/packages/usePackagePreview';
import { buildPhoneItems } from '../components/packages/packagePhoneItems';
import PackageEditorHeader, { type PackageEditorBusy, type PackageTransition } from '../components/packages/PackageEditorHeader';
import PackageNotices from '../components/packages/PackageNotices';
import PackageTextFields from '../components/packages/PackageTextFields';
import PackageServicePicker from '../components/packages/PackageServicePicker';
import PackageChoiceFixer from '../components/packages/PackageChoiceFixer';
import PackagePriceBox from '../components/packages/PackagePriceBox';
import PackageWindowFields from '../components/packages/PackageWindowFields';
import PackagePreviewPane from '../components/packages/PackagePreviewPane';

const TRANSITIONS: Record<PackageStatus, PackageTransition[]> = {
  DRAFT: [], PENDING_APPROVAL: ['withdraw'], SCHEDULED: ['pause', 'end'], LIVE: ['pause', 'end'],
  PAUSED: ['resume', 'end'], ENDED: [], REJECTED: [], SUSPENDED: [],
};

/** Spec 081b — two-pane package editor (form left, phone preview right). Routes /promotions/packages/new|:id. */
export default function PackageEditorPage() {
  const { id: routeId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { token } = useAuth();
  const { t, lang } = usePackagesT();
  const today = useMemo(() => todayInDubai(), []);
  const isNewRoute = !routeId || routeId === 'new';

  const [pkg, setPkg] = useState<PackageResponse | null>(null);
  const [form, setForm] = useState<PackageForm>(emptyPackageForm);
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(!isNewRoute);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<PackageFieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState<PackageEditorBusy>(null);
  const [confirm, setConfirm] = useState<PackageTransition | null>(null);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [catalogue, setCatalogue] = useState<OfferingV2Response[] | null>(null);
  const [catalogueError, setCatalogueError] = useState(false);
  const [garageName, setGarageName] = useState('');
  const [previewLang, setPreviewLang] = useState<'en' | 'ar'>(lang);
  const [previewTheme, setPreviewTheme] = useState<'light' | 'dark'>('light');

  const applyPackage = useCallback((p: PackageResponse, keepCover?: PackageForm) => {
    setPkg(p);
    const next = formFromPackage(p);
    // A fresh upload has a local preview URL; keep it when the server sends no download URL.
    if (keepCover && !next.coverUrl && next.coverStorageKey === keepCover.coverStorageKey) next.coverUrl = keepCover.coverUrl;
    setForm(next);
    setDirty(false);
  }, []);

  useEffect(() => {
    if (!token || isNewRoute || !routeId) return undefined;
    if (pkg?.id === routeId) return undefined; // just created here; state is current
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    api.getMyPackage(routeId, token)
      .then((p) => { if (!cancelled) applyPackage(p); })
      .catch((err) => { if (!cancelled) setLoadError(mapPackageError(err, t, [], () => '').code === 'PACKAGE_NOT_FOUND' ? t('packages.err.notFound') : t('packages.editor.loadFailed')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeId, token]);

  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    api.listMyOfferingsV2(token)
      .then((list) => { if (!cancelled) setCatalogue(list ?? []); })
      .catch(() => { if (!cancelled) setCatalogueError(true); });
    api.getMyGarage(token)
      .then((g) => { if (!cancelled) setGarageName(g?.name ?? ''); })
      .catch(() => { /* the preview falls back to an empty name */ });
    return () => { cancelled = true; };
  }, [token]);

  useEffect(() => {
    if (!toast) return undefined;
    const id = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(id);
  }, [toast]);

  // ── Derived state ──
  const catalogueMap = useMemo(() => new Map((catalogue ?? []).map((o) => [o.id, o])), [catalogue]);
  const status = pkg?.status ?? null;
  const readOnly = status === 'PENDING_APPROVAL' || status === 'ENDED';
  const isRunning = !!status && RUNNING_STATUSES.includes(status);
  const canSubmit = !readOnly && (!status || SUBMITTABLE_STATUSES.includes(status));

  const previewRequest = useMemo(() => (form.items.length > 0 ? packageFormToPreviewRequest(form) : null), [form]);
  const preview = usePackagePreview(token, previewRequest);
  const mode = preview.fresh && preview.data ? preview.data.pricingMode : derivePricingMode(form.items, catalogueMap);
  // Numbers: the fresh preview, else the saved package while untouched, else the last preview.
  const numbers = (preview.fresh ? preview.data : null) ?? (pkg && !dirty ? pkg : preview.data);
  const originalTotal = numbers?.originalTotal ?? null;

  const serverItems = useMemo(() => preview.data?.items ?? pkg?.items ?? [], [preview.data, pkg]);
  const nameOf = useCallback((id: string) => catalogueMap.get(id)?.name
    ?? serverItems.find((i) => i.offeringId === id)?.name ?? '—', [catalogueMap, serverItems]);
  const reasonOf = useCallback((id: string) => {
    const o = catalogueMap.get(id);
    return o ? ineligibleReasonOf(o) : (serverItems.find((i) => i.offeringId === id)?.ineligibleReason ?? null);
  }, [catalogueMap, serverItems]);

  // Inline problems from the preview (R3/R4), under any client/API error for the same field. The
  // preview's item-count error is left to the Submit hint so it does not nag while picking.
  const shownErrors: PackageFieldErrors = useMemo(() => {
    const fromPreview = preview.fresh ? previewErrorsToFields(preview.data?.errors, t, nameOf, reasonOf) : {};
    delete fromPreview.items;
    return { ...fromPreview, ...fieldErrors };
  }, [preview.fresh, preview.data, t, nameOf, reasonOf, fieldErrors]);

  // ── Field updates (each clears its own errors) ──
  const touch = (fields: PackageField[]) => {
    setDirty(true);
    setFormError(null);
    setFieldErrors((e) => {
      const next = { ...e };
      fields.forEach((f) => { delete next[f]; });
      return next;
    });
  };
  const update = <K extends keyof PackageForm>(key: K, value: PackageForm[K], fields: PackageField[]) => {
    setForm((f) => ({ ...f, [key]: value }));
    touch(fields);
  };
  const toggleItem = (offeringId: string) => {
    setForm((f) => {
      if (f.items.some((i) => i.offeringId === offeringId)) return { ...f, items: f.items.filter((i) => i.offeringId !== offeringId) };
      if (f.items.length >= ITEMS_MAX) return f;
      return { ...f, items: [...f.items, { offeringId, fixedValueIds: [] }] };
    });
    touch(['items', packageItemField(offeringId), 'packagePrice', 'discountPercent']);
  };
  const changeItem = (next: PackageFormItem) => {
    setForm((f) => ({ ...f, items: f.items.map((i) => (i.offeringId === next.offeringId ? next : i)) }));
    touch([packageItemField(next.offeringId), 'packagePrice', 'discountPercent']);
  };

  // ── Validation, save, submit, transitions ──
  // A running or SUSPENDED package keeps its (possibly past) start while it is unchanged, as the server allows.
  const startMayStayPast = isRunning || status === 'SUSPENDED';
  const windowCtx = { today, startLocked: startMayStayPast && !!pkg && form.startsAt === (pkg.startsAt ?? '').slice(0, 10) };
  const validate = (withPrice: boolean): PackageFieldErrors => ({
    ...validateTexts(form), ...validateItemsCount(form), ...validateWindow(form.startsAt, form.endsAt, windowCtx),
    ...(withPrice ? validatePrice(form, mode, originalTotal) : {}),
  });
  const showClientErrors = (errs: PackageFieldErrors) => {
    const translated: PackageFieldErrors = {};
    (Object.keys(errs) as PackageField[]).forEach((k) => { translated[k] = t(errs[k] as string); });
    setFieldErrors(translated);
    setFormError(t('packages.err.fixBelow'));
  };
  const showApiError = (err: unknown) => {
    const m = mapPackageError(err, t, form.items.map((i) => i.offeringId), nameOf);
    setFieldErrors(m.fieldErrors);
    setFormError(m.formError ?? (Object.keys(m.fieldErrors).length ? t('packages.err.fixBelow') : null));
  };

  /** Creates the draft or PUTs the changes (version sent). Returns the fresh package, or null on failure. */
  const persist = async (): Promise<PackageResponse | null> => {
    if (!token) return null;
    if (pkg && !dirty) return pkg;
    try {
      const body = packageFormToRequest(form, mode, pkg?.version);
      const saved = pkg ? await api.updatePackage(pkg.id, body, token) : await api.createPackage(body, token);
      applyPackage(saved, form);
      if (!pkg) navigate(`/promotions/packages/${saved.id}`, { replace: true });
      return saved;
    } catch (err) {
      showApiError(err);
      return null;
    }
  };

  const save = async () => {
    if (busy || readOnly) return;
    // Running packages change in place, so they pass the same checks as a submit (Q3 = B).
    const errs = validate(isRunning);
    if (Object.keys(errs).length) { showClientErrors(errs); return; }
    setBusy('save');
    const saved = await persist();
    setBusy(null);
    if (saved) {
      setFieldErrors({});
      setToast(isRunning ? t('packages.bar.changesSaved') : t('packages.bar.saved'));
    }
  };

  const submit = async () => {
    if (busy || !canSubmit || !token || form.items.length < ITEMS_MIN) return;
    const errs = validate(true);
    if (Object.keys(errs).length) { showClientErrors(errs); return; }
    setBusy('submit');
    try {
      const saved = await persist();
      if (!saved) return;
      try {
        applyPackage(await api.submitPackage(saved.id, token, saved.version));
      } catch (err) {
        showApiError(err);
        return;
      }
      setFieldErrors({});
      setFormError(null);
      setToast(t('packages.bar.submitted'));
    } finally {
      setBusy(null);
    }
  };

  const runTransition = async () => {
    if (!confirm || !pkg || !token) return;
    const fn = { withdraw: api.withdrawPackage, pause: api.pausePackage, resume: api.resumePackage, end: api.endPackage }[confirm];
    setBusy(confirm);
    setConfirmError(null);
    try {
      const next = await fn(pkg.id, token, pkg.version);
      if (dirty) setPkg(next); else applyPackage(next); // keep unsaved edits
      setToast(confirm === 'withdraw' ? t('packages.bar.withdrawn') : t('packages.toast.updated'));
      setConfirm(null);
    } catch (err) {
      setConfirmError(packageActionErrorMessage(err, t));
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return <div className="pkg-editor"><p className="offer-muted-center" role="status">{t('packages.loading')}</p></div>;
  }
  if (loadError) {
    return (
      <div className="pkg-editor">
        <button type="button" className="offer-back" onClick={() => navigate('/promotions?tab=packages')}>← {t('packages.editor.back')}</button>
        <div className="error-message" role="alert">{loadError}</div>
      </div>
    );
  }

  const typedPrice = Number(form.packagePrice);
  const typedPercent = Number(form.discountPercent);
  const section = (id: string, title: string, body: ReactNode) => (
    <section className="offer-card pkg-section" aria-labelledby={`pkg-sec-${id}`}>
      <h3 className="pkg-section__title" id={`pkg-sec-${id}`}>{title}</h3>
      {body}
    </section>
  );

  return (
    <div className="pkg-editor">
      <PackageEditorHeader
        t={t} lang={lang} title={pkg ? t('packages.editor.editTitle') : t('packages.editor.newTitle')} status={status} busy={busy}
        canSave={!readOnly} saveLabel={isRunning ? t('packages.bar.saveChanges') : t('packages.bar.saveDraft')}
        canSubmit={canSubmit} submitBlocked={form.items.length < ITEMS_MIN} transitions={status ? TRANSITIONS[status] : []}
        onBack={() => navigate('/promotions?tab=packages')} onSave={save} onSubmit={submit}
        onTransition={(a) => { setConfirmError(null); setConfirm(a); }}
      />
      <PackageNotices t={t} lang={lang} pkg={pkg} />
      {formError && <div className="error-message offer-form-error pkg-form-error" role="alert">{formError}</div>}

      <div className="pkg-editor__panes">
        <div className="pkg-editor__form">
          {section('details', t('packages.section.details'), (
            <PackageTextFields t={t} values={form} disabled={readOnly} fieldErrors={shownErrors}
              onChange={(key, value) => update(key, value, [key])} />
          ))}
          {section('cover', t('packages.section.cover'), (
            <BannerUploader token={token ?? ''} label={t('packages.cover.label')} hint={t('packages.cover.hint')} suggestions
              value={{ storageKey: form.coverStorageKey, url: form.coverUrl }} disabled={readOnly} error={shownErrors.cover} testId="pkg-cover"
              onChange={(v) => { setForm((f) => ({ ...f, coverStorageKey: v.storageKey, coverUrl: v.url })); touch(['cover']); }} />
          ))}
          {section('services', t('packages.section.services'), (
            <>
              <PackageServicePicker t={t} catalogue={catalogue} loadError={catalogueError} selectedIds={form.items.map((i) => i.offeringId)}
                nameOf={nameOf} disabled={readOnly} fieldErrors={shownErrors} onToggle={toggleItem} />
              {form.items.map((item) => (
                <PackageChoiceFixer key={item.offeringId} t={t} lang={lang} item={item} offering={catalogueMap.get(item.offeringId)}
                  name={nameOf(item.offeringId)} disabled={readOnly} error={shownErrors[packageItemField(item.offeringId)]} onChange={changeItem} />
              ))}
            </>
          ))}
          {section('price', t('packages.section.price'), (
            <PackagePriceBox t={t} mode={mode} preview={preview} itemCount={form.items.length} packagePrice={form.packagePrice}
              discountPercent={form.discountPercent} disabled={readOnly} fieldErrors={shownErrors}
              onPriceChange={(v) => update('packagePrice', v, ['packagePrice'])}
              onPercentChange={(v) => update('discountPercent', v, ['discountPercent'])} />
          ))}
          {section('window', t('packages.section.window'), (
            <PackageWindowFields t={t} startsAt={form.startsAt} endsAt={form.endsAt} today={today} startLocked={windowCtx.startLocked}
              disabled={readOnly} fieldErrors={shownErrors} onChange={(key, value) => update(key, value, ['startsAt', 'endsAt'])} />
          ))}
        </div>

        <PackagePreviewPane
          t={t} onLangChange={setPreviewLang} onThemeChange={setPreviewTheme}
          phone={{
            lang: previewLang, theme: previewTheme, garageName, nameEn: form.nameEn, nameAr: form.nameAr,
            descriptionEn: form.descriptionEn, descriptionAr: form.descriptionAr, coverUrl: form.coverUrl, mode, originalTotal,
            packagePrice: form.packagePrice.trim() !== '' && Number.isFinite(typedPrice) ? typedPrice : (numbers?.packagePrice ?? null),
            savingAmount: fixedSaving(originalTotal, form.packagePrice)?.amount ?? numbers?.savingAmount ?? null,
            savingPercent: fixedSaving(originalTotal, form.packagePrice)?.pct ?? numbers?.savingPercent ?? null,
            fromPrice: numbers?.fromPrice ?? null,
            discountPercent: form.discountPercent.trim() !== '' && Number.isFinite(typedPercent) ? typedPercent : (numbers?.discountPercent ?? null),
            endsAt: form.endsAt || null,
            items: buildPhoneItems(form.items, catalogueMap, serverItems),
          }}
        />
      </div>

      {confirm && (
        <ConfirmDialog
          title={t(`packages.confirm.${confirm}.title`)} body={t(`packages.confirm.${confirm}.body`)}
          confirmLabel={t(`packages.action.${confirm}`)} cancelLabel={t('packages.confirm.cancel')}
          danger={confirm === 'end'} busy={busy === confirm} error={confirmError}
          onConfirm={runTransition} onCancel={() => setConfirm(null)}
        />
      )}
      {toast && <div className="partner-toast success" role="status">{toast}</div>}
    </div>
  );
}
